import { UnitHistory } from '../unit-specific-facts';

type SpeedFacts = {
  [key: string]: UnitHistory;
};

export const speedFacts: SpeedFacts = {
  "meters per second": {
    name: "Meters per Second",
    shortDescription: "The SI unit of speed",
    history: "Meters per second (m/s) represents the marriage of distance and time measurement, emerging from the scientific revolution's need for precise motion description. Early measurements used water clocks and measured distances, but the true breakthrough came with chronographs that could measure split seconds. This precision enabled the first accurate measurements of sound speed and laid the groundwork for modern physics.",
    origin: "Derived from the fundamental SI units of meter and second, representing motion in its most basic form",
    modernUsage: "Beyond its scientific applications, m/s gained particular importance in ballistics and sports science, where precise speed measurements can mean the difference between victory and defeat",
    funFacts: [
      "The first accurate measurement of sound speed in 1738 used cannon fire and distant observers with precise pendulum clocks",
      "Early attempts to measure bullet speeds used rotating discs that bullets would pierce, creating a timing system accurate to microseconds",
      "The development of atomic clocks and laser rangefinders has made m/s measurements accurate to within millionths of a unit"
    ],
    related: ["kilometers per hour", "miles per hour"],
    images: []
  },
  "kilometers per hour": {
    name: "Kilometers per Hour",
    shortDescription: "A common unit of speed for vehicles",
    history: "Kilometers per hour (km/h) became standard as nations adopted metric measurements. Its relationship to meters per second (1 m/s = 3.6 km/h) reflects the convenience of different scales for different applications. The unit gained prominence with the spread of automobiles and metric road signs.",
    origin: "Derived from the metric units of kilometer and hour",
    modernUsage: "Modern speed cameras and GPS devices typically measure in km/h, though some convert to local preferences for display",
    funFacts: [
      "One kilometer per hour equals 0.621 miles per hour",
      "Used on road signs in most countries",
      "Standard unit for vehicle speedometers worldwide"
    ],
    related: ["meters per second", "miles per hour"],
    images: []
  },
  "miles per hour": {
    name: "Miles per Hour",
    shortDescription: "The standard unit of speed in the US and UK",
    history: "Miles per hour (mph) emerged with the development of mechanical transportation. Early railway companies played a crucial role in standardizing speed measurement, as consistent timetables required reliable speed calculations. The first speedometers, developed for automobiles in the 1890s, used centrifugal force to measure mph, with magnetic speedometers appearing in the 1920s.",
    origin: "Based on the imperial units of mile and hour",
    modernUsage: "The unit remains dominant in American and British road speed measurement, despite metric adoption in other fields",
    funFacts: [
      "First measured by railway companies",
      "Early speedometers used centrifugal force",
      "Standard for road speeds in US and UK"
    ],
    related: ["kilometers per hour", "knot"],
    images: []
  },
  "knot": {
    name: "Knot",
    shortDescription: "A unit of speed used in maritime and aviation",
    history: "The knot, equal to one nautical mile per hour, originated from the method used to measure a ship's speed: the log line. This ingenious device consisted of a wooden board attached to a line with knots tied at regular intervals. Sailors would count the knots that passed through their hands in a specific time (measured with a sand glass), giving the ship's speed. The standard spacing between knots was calibrated to give speed directly in nautical miles per hour.",
    origin: "From historical maritime navigation practices",
    modernUsage: "Used in maritime and aviation for speed measurement",
    funFacts: [
      "One knot equals one nautical mile per hour",
      "Originally measured using knotted rope and sand glass",
      "Still standard in maritime and aviation industries"
    ],
    related: ["nautical mile", "miles per hour"],
    images: []
  },
  "furlong per fortnight": {
    name: "Furlong per Fortnight",
    shortDescription: "A humorous unit of speed",
    history: "Though rarely used seriously, the furlong per fortnight (about 0.00595 miles per hour) became a favorite unit among scientists and mathematicians as an example of combining unusual but legitimate units. Its extremely slow rate made it useful for measuring continental drift and other geological processes. The unit gained popularity in academic circles as a humorous way to demonstrate the flexibility of traditional measurement systems.",
    origin: "Combination of historical units for humorous effect",
    modernUsage: "Used humorously to illustrate impractical units and occasionally in geological measurements",
    funFacts: [
      "Used to measure continental drift",
      "Equals about 0.00595 miles per hour",
      "Popular in academic demonstrations"
    ],
    related: ["furlong", "fortnight"],
    images: []
  }
};
